import { useI18n } from '@/i18n'
import { CheckCircle } from '@/shared/icons/phosphor'
import { For } from 'solid-js'

type Grade = 'S' | 'A' | 'B'

const gradeClass = (grade: Grade) => {
  if (grade === 'S') {
    return 'bg-[var(--color-secondary-500)] text-white'
  }
  if (grade === 'A') {
    return 'bg-[var(--color-secondary-100)] text-[var(--color-secondary-700)]'
  }
  return 'bg-[var(--color-neutral-100)] text-[var(--color-text-muted)]'
}

export function HiringManagerPromoPanel() {
  const { t } = useI18n()

  const benefits = () => [
    {
      title: t('auth.hiringManager.promo.benefit1.title'),
      desc: t('auth.hiringManager.promo.benefit1.desc'),
    },
    {
      title: t('auth.hiringManager.promo.benefit2.title'),
      desc: t('auth.hiringManager.promo.benefit2.desc'),
    },
    {
      title: t('auth.hiringManager.promo.benefit3.title'),
      desc: t('auth.hiringManager.promo.benefit3.desc'),
    },
    {
      title: t('auth.hiringManager.promo.benefit4.title'),
      desc: t('auth.hiringManager.promo.benefit4.desc'),
    },
  ]

  const candidates: { initials: string; grade: Grade; fit: number }[] = [
    { initials: 'AM', grade: 'S', fit: 96 },
    { initials: 'SO', grade: 'A', fit: 88 },
    { initials: 'LC', grade: 'B', fit: 78 },
  ]

  return (
    <div class="mx-auto flex h-full w-full max-w-md flex-col justify-center px-5 py-8 lg:px-8">
      <h2
        data-ac-item
        class="font-[family-name:var(--font-display)] text-[length:var(--text-xl)] font-[var(--font-bold)] leading-[var(--leading-display)] text-[var(--color-text)] lg:text-[length:var(--text-2xl)]"
      >
        {t('auth.hiringManager.promo.headline')}
      </h2>
      <p data-ac-item class="mt-2 text-[length:var(--text-sm)] text-[var(--color-text-muted)]">
        {t('auth.hiringManager.promo.subhead')}
      </p>

      <ul class="mt-4 space-y-2 lg:mt-5">
        <For each={benefits()}>
          {(b) => (
            <li
              data-ac-item
              class="ac-glass-chip flex items-start gap-2.5 rounded-[var(--radius-12)] px-3.5 py-2.5"
            >
              <CheckCircle
                weight="fill"
                size={18}
                class="shrink-0 mt-0.5 text-[var(--color-secondary-600)]"
              />
              <div>
                <p class="text-[length:var(--text-sm)] font-[var(--font-semibold)] text-[var(--color-text)]">
                  {b.title}
                </p>
                <p class="text-[length:var(--text-xs)] text-[var(--color-text-muted)]">{b.desc}</p>
              </div>
            </li>
          )}
        </For>
      </ul>

      <div
        data-ac-item
        data-ac-promo-extra
        class="ac-glass-card mt-5 hidden rounded-[var(--radius-lg)] p-4 lg:block"
      >
        <p class="text-[length:var(--text-sm)] font-[var(--font-medium)] text-[var(--color-text)]">
          {t('auth.hiringManager.promo.card.label')}
        </p>
        <p class="mt-0.5 text-[length:var(--text-xs)] text-[var(--color-text-muted)]">
          {t('auth.hiringManager.promo.card.caption')}
        </p>
        <div class="mt-4 space-y-3">
          <For each={candidates}>
            {(c) => (
              <div class="flex items-center gap-3">
                <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-secondary-100)] text-[length:var(--text-xs)] font-[var(--font-semibold)] text-[var(--color-secondary-700)]">
                  {c.initials}
                </div>
                <div class="h-1.5 flex-1 rounded-full bg-[var(--color-neutral-100)]">
                  <div
                    class="h-1.5 rounded-full bg-[var(--color-secondary-500)]"
                    style={{ width: `${c.fit}%` }}
                  />
                </div>
                <span
                  class={`flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-8)] text-[length:var(--text-xs)] font-[var(--font-bold)] ${gradeClass(c.grade)}`}
                >
                  {c.grade}
                </span>
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  )
}
